// Global Instructions Rule Applied!
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

// Internal Agents — Phase 2 UI (design: docs/INTERNAL_AGENTS_DESIGN.md).
// Response shapes of the /internal-agents/* read endpoints, strict-validated
// before any UI state (a response that fails validation is an error, never
// coerced). Keys follow the RPC output (table__column convention).
//
// NOTE: legacy ids can predate uuid formatting — use z.string(), never
// z.string().uuid() (bulk-add-prospects precedent).

// ── signals (view__agent_signals) ───────────────────────────────────────────

export const AgentSignalEvidenceSchema = z
  .object({
    interactions__id: z.string(),
    interactions__type: z.string().nullable(),
    interactions__subject: z.string().nullable(),
    interactions__datetime: z.string().nullable(),
    excerpt: z.string().nullable(),
    // The record whose Dashboard timeline shows this interaction (the
    // signal's own context when linked there, else the first live link —
    // interaction__timeline_ref). Null = unlinked, rendered as text.
    timeline_table: z.string().nullable().optional(),
    timeline_id: z.string().nullable().optional()
  })
  .strict()

export const AgentSignalSchema = z
  .object({
    agent_signals__id: z.string(),
    agent_signals__context_table: z.string(),
    agent_signals__context_id: z.string(),
    subject_name: z.string().nullable(),
    kind_key: z.string(),
    kind_label: z.string(),
    agent_signals__severity: z.string(),
    agent_signals__title: z.string(),
    agent_signals__summary: z.string().nullable(),
    agent_signals__confidence: z.number().nullable(),
    // Two-arg z.record: Zod 4 dropped the single-arg form, and the one-arg
    // call throws at PARSE time (not construction) the moment the record has
    // any key — so an empty {} payload passed and a populated one crashed the
    // whole array parse.
    agent_signals__payload: z.record(z.string(), z.unknown()).nullable(),
    agent_signals__status: z.string(),
    agent_signals__status_reason: z.string().nullable(),
    agent_signals__fired_at: z.string(),
    agent_runs__id: z.string().nullable(),
    agent_type_key: z.string().nullable(),
    // The task a person raised from this signal. Joined live-only, so these
    // go null again if the task is later deleted.
    agent_signals__task_id: z.string().nullable(),
    tasks__subject: z.string().nullable(),
    tasks__completed: z.boolean().nullable(),
    tasks__due_by: z.string().nullable(),
    evidence: z.array(AgentSignalEvidenceSchema)
  })
  .strict()

// ── memories (view__agent_memories) ─────────────────────────────────────────

export const AgentMemorySchema = z
  .object({
    agent_memories__id: z.string(),
    agent_memories__memory_type: z.string(),
    agent_memories__content: z.string(),
    agent_memories__structured: z.record(z.string(), z.unknown()).nullable(),
    agent_memories__importance: z.number(),
    agent_memories__status: z.string(),
    agent_memories__superseded_by_id: z.string().nullable(),
    agent_memories__source_interaction_id: z.string().nullable(),
    // Where the source interaction is opened (see AgentSignalEvidenceSchema).
    agent_memories__source_timeline_table: z.string().nullable().optional(),
    agent_memories__source_timeline_id: z.string().nullable().optional(),
    agent_runs__id: z.string().nullable(),
    agent_memories__last_confirmed_at: z.string().nullable(),
    agent_memories__date_created: z.string()
  })
  .strict()

// ── instances (view__agent_instances) ───────────────────────────────────────

export const AgentInstanceSchema = z
  .object({
    agent_instances__id: z.string(),
    agent_type_key: z.string(),
    agent_instances__subject_table: z.string(),
    agent_instances__subject_id: z.string(),
    subject_name: z.string().nullable(),
    agent_instances__status: z.string(),
    agent_instances__last_processed_interaction_at: z.string().nullable(),
    agent_instances__follow_until: z.string().nullable(),
    prog__open_signals: z.number(),
    prog__active_memories: z.number(),
    prog__runs: z.number()
  })
  .strict()

// ── runs (view__agent_runs) ─────────────────────────────────────────────────

export const AgentRunLlmCallSchema = z
  .object({
    task_key: z.string(),
    llm_model_key: z.string().nullable(),
    provider: z.string().nullable(),
    model_name: z.string().nullable(),
    input_tokens: z.number(),
    output_tokens: z.number(),
    cost_usd: z.number(),
    latency_ms: z.number().nullable(),
    status: z.string(),
    error: z.string().nullable()
  })
  .strict()

export const AgentRunSchema = z
  .object({
    agent_runs__id: z.string(),
    agent_instances__id: z.string(),
    agent_type_key: z.string(),
    agent_runs__trigger: z.string(),
    agent_runs__status: z.string(),
    agent_runs__attempts: z.number(),
    agent_runs__window_from: z.string().nullable(),
    agent_runs__window_to: z.string().nullable(),
    agent_runs__summary: z.string().nullable(),
    agent_runs__total_input_tokens: z.number(),
    agent_runs__total_output_tokens: z.number(),
    agent_runs__total_cost_usd: z.number(),
    agent_runs__error: z.string().nullable(),
    agent_runs__date_started: z.string().nullable(),
    agent_runs__date_finished: z.string().nullable(),
    agent_runs__date_created: z.string(),
    llm_calls: z.array(AgentRunLlmCallSchema)
  })
  .strict()

// ── company brief (view__company_brief) ─────────────────────────────────────
// Left-joined against cfg sections: an unwritten section has null content.

export const CompanyBriefSectionSchema = z
  .object({
    company_brief_sections__section_key: z.string(),
    company_brief_sections__label: z.string(),
    company_brief_sections__priority: z.number(),
    company_briefs__id: z.string().nullable(),
    company_briefs__content: z.string().nullable(),
    company_briefs__generated_at: z.string().nullable(),
    agent_runs__id: z.string().nullable(),
    agent_instances__id: z.string().nullable(),
    agent_type_key: z.string().nullable()
  })
  .strict()

// ── deal brief (view__deal_brief) ───────────────────────────────────────────
// Same left-joined shape as the company brief, against cfg.deal_brief_sections.

export const DealBriefSectionSchema = z
  .object({
    deal_brief_sections__section_key: z.string(),
    deal_brief_sections__label: z.string(),
    deal_brief_sections__priority: z.number(),
    deal_briefs__id: z.string().nullable(),
    deal_briefs__content: z.string().nullable(),
    deal_briefs__generated_at: z.string().nullable(),
    agent_runs__id: z.string().nullable(),
    agent_instances__id: z.string().nullable(),
    agent_type_key: z.string().nullable()
  })
  .strict()

// ── LP brief (view__lp_brief; LP agent programme Phase 3) ──────────────────

export const LpBriefSectionSchema = z
  .object({
    lp_brief_sections__section_key: z.string(),
    lp_brief_sections__label: z.string(),
    lp_brief_sections__priority: z.number(),
    lp_brief_sections__external_safe: z.boolean(),
    lp_briefs__id: z.string().nullable(),
    lp_briefs__content: z.string().nullable(),
    lp_briefs__generated_at: z.string().nullable(),
    agent_runs__id: z.string().nullable(),
    agent_instances__id: z.string().nullable(),
    agent_type_key: z.string().nullable()
  })
  .strict()

// ── LP fundraising position (view__lp_fundraising_position) ─────────────────
// One row per OPEN fundraising process on an LP account. `flags` and
// `whose_move` are the prospect tracker's own deterministic reading, so the
// panel and the agent's signals can never disagree.
export const LpFundraisingRowSchema = z
  .object({
    prospects__id: z.string(),
    prospects__name: z.string().nullable(),
    prospects__commitment: z.number().nullable(),
    prospects__commitment_fmt: z.string().nullable(),
    currencies__code: z.string().nullable(),
    funds__id: z.string().nullable(),
    funds__name: z.string().nullable(),
    pipelines__name: z.string().nullable(),
    pipeline_stages__name: z.string().nullable(),
    pipeline_statuses__name: z.string().nullable(),
    pipeline_prospects__stage_date: z.string().nullable(),
    days_in_stage: z.number().nullable(),
    last_activity_at: z.string().nullable(),
    our_last_move_at: z.string().nullable(),
    days_since_our_last_move: z.number().nullable(),
    unanswered_inbound_days: z.number().nullable(),
    whose_move: z.string(),
    flags: z.array(z.string()),
    open_signals_count: z.number(),
    next_step: z.string().nullable(),
    next_step_at: z.string().nullable(),
    agent_instances__id: z.string().nullable(),
    agent_instances__status: z.string().nullable()
  })
  .strict()

// ── LP fundraising history (view__lp_fundraising_history) ───────────────────
// One row per fundraising process the LP has EVER been in, whatever came of
// it. The Fundraising card's counterpart: that one is action items and shows
// only open processes, this one is the record — Phase 10 stops a closed fund's
// prospects out of the card, and the outreach that went into them lives here.
export const LpFundraisingHistoryRowSchema = z
  .object({
    prospects__id: z.string(),
    prospects__name: z.string().nullable(),
    prospects__date_created: z.string().nullable(),
    prospects__commitment: z.number().nullable(),
    prospects__commitment_fmt: z.string().nullable(),
    currencies__code: z.string().nullable(),
    funds__id: z.string().nullable(),
    funds__name: z.string().nullable(),
    fund_statuses__name: z.string().nullable(),
    fund_statuses__is_open: z.boolean().nullable(),
    // When the fund's fundraising status was RECORDED (from the audit trail —
    // nothing stores it on the fund). Null where it was never changed through
    // the app, including every fund with no fundraising status at all.
    funds__fundraising_status_since: z.string().nullable(),
    pipelines__name: z.string().nullable(),
    pipeline_stages__name: z.string().nullable(),
    pipeline_statuses__name: z.string().nullable(),
    pipeline_prospects__stage_date: z.string().nullable(),
    fund_commitments__amount: z.number().nullable(),
    fund_commitments__amount_fmt: z.string().nullable(),
    interactions_count: z.number(),
    last_interaction_at: z.string().nullable(),
    last_interaction_type: z.string().nullable(),
    agent_instances__status: z.string().nullable(),
    agent_instances__unfollow_reason: z.string().nullable(),
    comms_lp_statuses__label: z.string().nullable(),
    comms_lp_statuses__tag_color: z.string().nullable(),
    // The outcome's KEY, and the label/colour to render for it — all three
    // from cfg.pipeline_outcomes via view__lp_fundraising_history. Not an enum
    // any more: the vocabulary is config, so an operator can add an outcome
    // without a frontend deploy, and a z.enum() would reject it at the door.
    // The keys SQL itself resolves are 'invested' and 'open'; everything else
    // is reached through the status's own outcome_id.
    outcome: z.string(),
    outcome_label: z.string().nullable(),
    outcome_tag_color: z.string().nullable()
  })
  .strict()

// ── prospect brief (view__prospect_brief) ───────────────────────────────────
// The LP brief's sibling on a fundraising process. No external_safe flag:
// nothing about a live process is quotable to the investor.
export const ProspectBriefSectionSchema = z
  .object({
    prospect_brief_sections__section_key: z.string(),
    prospect_brief_sections__label: z.string(),
    prospect_brief_sections__priority: z.number(),
    prospect_briefs__id: z.string().nullable(),
    prospect_briefs__content: z.string().nullable(),
    prospect_briefs__generated_at: z.string().nullable(),
    agent_runs__id: z.string().nullable(),
    agent_instances__id: z.string().nullable(),
    agent_type_key: z.string().nullable()
  })
  .strict()

// ── investment criteria (view__investment_criteria_admin) ───────────────────

export const InvestmentCriterionSchema = z
  .object({
    investment_criteria__id: z.string(),
    investment_criteria__criterion_key: z.string(),
    investment_criteria__label: z.string(),
    investment_criteria__guidance: z.string().nullable(),
    investment_criteria__priority: z.number(),
    investment_criteria__deal_type_id: z.string().nullable(),
    deal_type_label: z.string().nullable(),
    investment_criteria__active: z.boolean()
  })
  .strict()

// ── written content (view__agent_written_content) ───────────────────────────

export const AgentWrittenContentSchema = z
  .object({
    output_type: z.string(),
    id: z.string(),
    agent_type_key: z.string().nullable(),
    subject_table: z.string().nullable(),
    subject_id: z.string().nullable(),
    subject_name: z.string().nullable(),
    title: z.string().nullable(),
    content: z.string().nullable(),
    status: z.string().nullable(),
    severity: z.string().nullable(),
    agent_run_id: z.string().nullable(),
    created_at: z.string()
  })
  .strict()

// ── company states (view__company_states, account-scoped) ───────────────────
// Persistent types always present (placeholder row with null id until the
// first state row exists); transitory/ask rows only while active. Fleet rows
// additionally carry accounts__name (optional here).

export const CompanyStateConditionSchema = z
  .object({
    condition_key: z.string(),
    label: z.string(),
    rank: z.number().nullable()
  })
  .strict()

export const CompanyStateSchema = z
  .object({
    company_states__id: z.string().nullable(),
    company_state_types__state_key: z.string(),
    company_state_types__label: z.string(),
    company_state_types__kind: z.string(),
    company_states__condition_key: z.string().nullable(),
    condition_label: z.string().nullable(),
    condition_rank: z.number().nullable(),
    company_states__summary: z.string().nullable(),
    company_states__confidence: z.number().nullable(),
    company_states__status: z.string().nullable(),
    company_states__started_at: z.string().nullable(),
    company_states__last_confirmed_at: z.string().nullable(),
    // A3b lifecycle: evidence date vs run date, transitory horizon
    company_states__last_evidence_at: z.string().nullable().optional(),
    company_states__last_checked_at: z.string().nullable().optional(),
    company_states__expected_end_at: z.string().nullable().optional(),
    company_states__expected_end_basis: z.string().nullable().optional(),
    company_states__user_set: z.boolean(),
    agent_runs__id: z.string().nullable(),
    accounts__id: z.string().nullable(),
    accounts__name: z.string().nullable().optional(),
    // The task a person raised from this state card (live-only join — a
    // deleted task disappears while the stamp remains server-side).
    // Optional so the frontend tolerates a backend one deploy behind.
    company_states__task_id: z.string().nullable().optional(),
    tasks__subject: z.string().nullable().optional(),
    tasks__completed: z.boolean().nullable().optional(),
    tasks__due_by: z.string().nullable().optional(),
    conditions: z.array(CompanyStateConditionSchema).optional()
  })
  .strict()

// ── deal states (view__deal_states) ─────────────────────────────────────────
// The deal-level port of company states (deal follower split): per-follower
// catalogues (agent_type_key names the owning follower), persistent
// placeholder rows (null id) for followers live on the deal, active
// transitory/ask rows, the conditions catalogue for the override picker, and
// the live-only linked task. Conditions reuse CompanyStateConditionSchema —
// identical shape by construction.
export const DealStateSchema = z
  .object({
    deal_states__id: z.string().nullable(),
    deal_state_types__state_key: z.string(),
    deal_state_types__label: z.string(),
    deal_state_types__kind: z.string(),
    agent_type_key: z.string().nullable(),
    deal_states__condition_key: z.string().nullable(),
    condition_label: z.string().nullable(),
    condition_rank: z.number().nullable(),
    deal_states__summary: z.string().nullable(),
    deal_states__confidence: z.number().nullable(),
    deal_states__status: z.string().nullable(),
    deal_states__started_at: z.string().nullable(),
    deal_states__last_confirmed_at: z.string().nullable(),
    deal_states__last_evidence_at: z.string().nullable().optional(),
    deal_states__last_checked_at: z.string().nullable().optional(),
    deal_states__expected_end_at: z.string().nullable().optional(),
    deal_states__expected_end_basis: z.string().nullable().optional(),
    deal_states__user_set: z.boolean(),
    agent_runs__id: z.string().nullable(),
    deals__id: z.string().nullable(),
    deals__name: z.string().nullable().optional(),
    deal_states__task_id: z.string().nullable().optional(),
    tasks__subject: z.string().nullable().optional(),
    tasks__completed: z.boolean().nullable().optional(),
    tasks__due_by: z.string().nullable().optional(),
    conditions: z.array(CompanyStateConditionSchema).optional()
  })
  .strict()

// ── prospect states (view__prospect_states) ─────────────────────────────────
// The prospect-level port of deal states (LP agent programme Phase 4b): the
// agent-maintained overlay on the human-owned pipeline status. Same row
// anatomy as DealStateSchema with the prospect_states__* key family; the
// fleet branch adds the investor / fund ids for rollups.
export const ProspectStateSchema = z
  .object({
    prospect_states__id: z.string().nullable(),
    prospect_state_types__state_key: z.string(),
    prospect_state_types__label: z.string(),
    prospect_state_types__kind: z.string(),
    agent_type_key: z.string().nullable(),
    prospect_states__condition_key: z.string().nullable(),
    condition_label: z.string().nullable(),
    condition_rank: z.number().nullable(),
    prospect_states__summary: z.string().nullable(),
    prospect_states__confidence: z.number().nullable(),
    prospect_states__status: z.string().nullable(),
    prospect_states__started_at: z.string().nullable(),
    prospect_states__last_confirmed_at: z.string().nullable(),
    prospect_states__last_evidence_at: z.string().nullable().optional(),
    prospect_states__last_checked_at: z.string().nullable().optional(),
    prospect_states__expected_end_at: z.string().nullable().optional(),
    prospect_states__expected_end_basis: z.string().nullable().optional(),
    prospect_states__user_set: z.boolean(),
    agent_runs__id: z.string().nullable(),
    prospects__id: z.string().nullable(),
    prospects__name: z.string().nullable().optional(),
    accounts__id: z.string().nullable().optional(),
    funds__id: z.string().nullable().optional(),
    prospect_states__task_id: z.string().nullable().optional(),
    tasks__subject: z.string().nullable().optional(),
    tasks__completed: z.boolean().nullable().optional(),
    tasks__due_by: z.string().nullable().optional(),
    conditions: z.array(CompanyStateConditionSchema).optional()
  })
  .strict()

// ── LP account states (view__lp_states) ─────────────────────────────────────
// The account-level port of prospect states (LP agent programme Phase 7): the
// durable half of an LP account's agent surface — relationship strength,
// stance, engagement cadence, open asks, key-person transitions. Same row
// anatomy with the lp_states__* key family; the fleet branch adds the account
// name for rollups.
export const LpStateSchema = z
  .object({
    lp_states__id: z.string().nullable(),
    lp_state_types__state_key: z.string(),
    lp_state_types__label: z.string(),
    lp_state_types__kind: z.string(),
    agent_type_key: z.string().nullable(),
    lp_states__condition_key: z.string().nullable(),
    condition_label: z.string().nullable(),
    condition_rank: z.number().nullable(),
    lp_states__summary: z.string().nullable(),
    lp_states__confidence: z.number().nullable(),
    lp_states__status: z.string().nullable(),
    lp_states__started_at: z.string().nullable(),
    lp_states__last_confirmed_at: z.string().nullable(),
    lp_states__last_evidence_at: z.string().nullable().optional(),
    lp_states__last_checked_at: z.string().nullable().optional(),
    lp_states__expected_end_at: z.string().nullable().optional(),
    lp_states__expected_end_basis: z.string().nullable().optional(),
    lp_states__user_set: z.boolean(),
    agent_runs__id: z.string().nullable(),
    accounts__id: z.string().nullable(),
    accounts__name: z.string().nullable().optional(),
    lp_states__task_id: z.string().nullable().optional(),
    tasks__subject: z.string().nullable().optional(),
    tasks__completed: z.boolean().nullable().optional(),
    tasks__due_by: z.string().nullable().optional(),
    conditions: z.array(CompanyStateConditionSchema).optional()
  })
  .strict()

// ── DocuSign envelopes (view__docusign_envelopes) ───────────────────────────
// Detected envelope emails: matched ones link a deal; unmatched ones await a
// manual link or dismissal in the Configuration panel.

export const DocusignEnvelopeSchema = z
  .object({
    docusign_envelopes__id: z.string(),
    docusign_envelopes__subject: z.string().nullable(),
    docusign_envelopes__sender_email: z.string().nullable(),
    docusign_envelopes__mailbox_email: z.string().nullable(),
    docusign_envelopes__received_at: z.string().nullable(),
    docusign_envelopes__status: z.string(),
    docusign_envelopes__match_method: z.string().nullable(),
    docusign_envelopes__matched_deal_id: z.string().nullable(),
    docusign_envelopes__interaction_id: z.string().nullable(),
    docusign_envelopes__date_created: z.string(),
    deals__name: z.string().nullable()
  })
  .strict()

// ── company KPIs (view__company_kpis) ───────────────────────────────────────
// One row per active account_kpi: resolved label/unit, latest approved value
// ('current', null until the first value lands), approved history ascending
// (sparkline material, capped at 24 points server-side).

export const CompanyKpiHistoryPointSchema = z
  .object({
    period_end: z.string(),
    value_numeric: z.number(),
    anomaly: z.boolean()
  })
  .strict()

export const CompanyKpiCurrentSchema = z
  .object({
    company_kpi_values__id: z.string(),
    company_kpi_values__period_type: z.string(),
    company_kpi_values__period_end: z.string(),
    company_kpi_values__value_numeric: z.number(),
    company_kpi_values__value_raw: z.string().nullable(),
    company_kpi_values__currency_code: z.string().nullable(),
    company_kpi_values__anomaly: z.boolean(),
    company_kpi_values__anomaly_reason: z.string().nullable(),
    company_kpi_values__confidence: z.number().nullable(),
    interactions__id: z.string().nullable(),
    company_kpi_values__date_created: z.string()
  })
  .strict()

export const CompanyKpiSchema = z
  .object({
    account_kpis__id: z.string(),
    kpi_key: z.string(),
    kpi_label: z.string(),
    is_custom: z.boolean(),
    unit_type: z.string(),
    direction: z.string(),
    account_kpis__period_type: z.string(),
    account_kpis__currency_code: z.string().nullable(),
    account_kpis__aliases: z.array(z.string()),
    current: CompanyKpiCurrentSchema.nullable(),
    history: z.array(CompanyKpiHistoryPointSchema)
  })
  .strict()

// ── admin panel: backfill manifest (controller.backfill) ────────────────────

// One subject in scope with its instance state and why it is (or is not) a
// backfill candidate. `reason` stays a plain string: alongside the fixed set
// (no_instance / already_queued / already_running / already_processed /
// eligible) the RPC also emits `instance_<status>` for any non-active status.
export const BackfillCandidateSchema = z
  .object({
    subject_id: z.string(),
    subject_name: z.string().nullable(),
    account_id: z.string().nullable(),
    account_name: z.string().nullable(),
    instance_id: z.string().nullable(),
    instance_status: z.string().nullable(),
    last_processed_interaction_at: z.string().nullable(),
    in_flight_run_id: z.string().nullable(),
    reason: z.string(),
    eligible: z.boolean()
  })
  .strict()

export const BackfillResultSchema = z
  .object({
    subject_id: z.string(),
    subject_name: z.string().nullable().optional(),
    instance_id: z.string().optional(),
    run_id: z.string().optional(),
    earliest_run_at: z.string().optional(),
    status: z.string()
  })
  .strict()

export const BackfillManifestSchema = z
  .object({
    scope: z.record(z.string(), z.string()),
    agent_type_key: z.string(),
    candidates_total: z.number(),
    eligible_total: z.number(),
    batch_size: z.number(),
    selected_total: z.number(),
    remaining_eligible: z.number(),
    skipped_counts: z.record(z.string(), z.number()),
    skipped: z.array(BackfillCandidateSchema),
    skipped_truncated: z.boolean(),
    dry_run: z.boolean(),
    enqueued: z.number(),
    results: z.array(BackfillResultSchema),
    // dry-run only
    selected: z.array(BackfillCandidateSchema).optional(),
    // enqueue only
    stagger_minutes: z.number().nullable().optional()
  })
  .strict()

// ── deepen backfill (view__agent_deepen_preview / internal_agents__enqueue_deepen)
// One shape for the dry run and the enqueue: the preview fields always, the
// enqueue fields (run_id, status) when a run was queued. reason ∈ eligible |
// no_instance | instance_<status> | type_disabled | not_window_type |
// already_queued | already_running | not_processed | history_complete |
// floor_not_before_ceiling | nothing_earlier.
export const DeepenPreviewSchema = z
  .object({
    instance_id: z.string(),
    agent_type_key: z.string().nullish(),
    subject_table: z.string().nullish(),
    subject_id: z.string().nullish(),
    subject_name: z.string().nullish(),
    instance_status: z.string().nullish(),
    watermark: z.string().nullish(),
    ceiling: z.string().nullish(),
    floor: z.string().nullish(),
    history_complete: z.boolean().nullish(),
    interactions_in_range: z.number().nullish(),
    windows_estimate: z.number().nullish(),
    window_limit: z.number().nullish(),
    total_interactions: z.number().nullish(),
    earliest_interaction_at: z.string().nullish(),
    in_flight_run_id: z.string().nullish(),
    in_flight_run_status: z.string().nullish(),
    reason: z.string(),
    eligible: z.boolean(),
    dry_run: z.boolean(),
    enqueued: z.number(),
    status: z.string().nullish(),
    run_id: z.string().nullish()
  })
  .strict()

// ── admin panel: fleet run log (view__agent_runs_admin) ─────────────────────

export const AdminAgentRunSchema = z
  .object({
    agent_runs__id: z.string(),
    agent_instances__id: z.string(),
    agent_type_key: z.string(),
    agent_instances__subject_table: z.string(),
    agent_instances__subject_id: z.string(),
    subject_name: z.string().nullable(),
    agent_runs__trigger: z.string(),
    agent_runs__run_mode: z.string().nullable(),
    agent_runs__status: z.string(),
    agent_runs__attempts: z.number(),
    agent_runs__earliest_run_at: z.string().nullable(),
    agent_runs__window_from: z.string().nullable(),
    agent_runs__window_to: z.string().nullable(),
    agent_runs__summary: z.string().nullable(),
    agent_runs__error: z.string().nullable(),
    // Failure bookkeeping: the classifier's verdict (transient / caps /
    // permanent / unknown + source) and the auto-retry chain position.
    agent_runs__error_class: z.string().nullable(),
    agent_runs__error_source: z.string().nullable(),
    agent_runs__retry_count: z.number(),
    agent_runs__retry_of: z.string().nullable(),
    agent_runs__retried_as: z.string().nullable(),
    agent_runs__total_cost_usd: z.number(),
    agent_runs__total_input_tokens: z.number(),
    agent_runs__total_output_tokens: z.number(),
    agent_runs__date_created: z.string(),
    agent_runs__date_started: z.string().nullable(),
    agent_runs__date_finished: z.string().nullable()
  })
  .strict()

// Display-only aggregate — typed, not Zod-validated, matching the
// gateway-usage / agent-access-admin precedent.
export interface AgentQueueSummary {
  by_status: {
    queued: number
    dispatched: number
    processing: number
    done: number
    skipped: number
    error: number
    unconfigured: number
  }
  queued_by_trigger: Record<string, number>
  queued_due_now: number
  next_due_at: string | null
  oldest_queued_at: string | null
  cron_jobs: Array<{ jobname: string; active: boolean; schedule: string }> | null
  as_of: string
}

// ── inferred types ──────────────────────────────────────────────────────────

export type AgentSignal = z.infer<typeof AgentSignalSchema>
export type AgentSignalEvidence = z.infer<typeof AgentSignalEvidenceSchema>
export type AgentMemory = z.infer<typeof AgentMemorySchema>
export type AgentInstance = z.infer<typeof AgentInstanceSchema>
export type AgentRun = z.infer<typeof AgentRunSchema>
export type AdminAgentRun = z.infer<typeof AdminAgentRunSchema>
export type BackfillCandidate = z.infer<typeof BackfillCandidateSchema>
export type BackfillManifest = z.infer<typeof BackfillManifestSchema>
export type DeepenPreview = z.infer<typeof DeepenPreviewSchema>
export type CompanyBriefSection = z.infer<typeof CompanyBriefSectionSchema>
export type DealBriefSection = z.infer<typeof DealBriefSectionSchema>
export type LpBriefSection = z.infer<typeof LpBriefSectionSchema>
export type ProspectBriefSection = z.infer<typeof ProspectBriefSectionSchema>
export type LpFundraisingRow = z.infer<typeof LpFundraisingRowSchema>
export type LpFundraisingHistoryRow = z.infer<typeof LpFundraisingHistoryRowSchema>
export type InvestmentCriterion = z.infer<typeof InvestmentCriterionSchema>
export type AgentWrittenContent = z.infer<typeof AgentWrittenContentSchema>
export type CompanyState = z.infer<typeof CompanyStateSchema>
export type DealState = z.infer<typeof DealStateSchema>
export type ProspectState = z.infer<typeof ProspectStateSchema>
export type LpState = z.infer<typeof LpStateSchema>
export type DocusignEnvelope = z.infer<typeof DocusignEnvelopeSchema>
export type CompanyStateCondition = z.infer<typeof CompanyStateConditionSchema>
export type CompanyKpi = z.infer<typeof CompanyKpiSchema>
export type CompanyKpiHistoryPoint = z.infer<typeof CompanyKpiHistoryPointSchema>

// ── validators ──────────────────────────────────────────────────────────────

export const validateAgentSignalsList = (data: unknown): AgentSignal[] =>
  validateWithZodSchema(z.array(AgentSignalSchema), data, 'agent signals list')

export const validateAgentMemoriesList = (data: unknown): AgentMemory[] =>
  validateWithZodSchema(z.array(AgentMemorySchema), data, 'agent memories list')

export const validateAgentInstancesList = (data: unknown): AgentInstance[] =>
  validateWithZodSchema(z.array(AgentInstanceSchema), data, 'agent instances list')

export const validateAgentRunsList = (data: unknown): AgentRun[] =>
  validateWithZodSchema(z.array(AgentRunSchema), data, 'agent runs list')

export const validateAdminAgentRunsList = (data: unknown): AdminAgentRun[] =>
  validateWithZodSchema(z.array(AdminAgentRunSchema), data, 'fleet agent runs list')

export const validateBackfillManifest = (data: unknown): BackfillManifest =>
  validateWithZodSchema(BackfillManifestSchema, data, 'agent backfill manifest')

export const validateDeepenPreview = (data: unknown): DeepenPreview =>
  validateWithZodSchema(DeepenPreviewSchema, data, 'agent deepen preview')

export const validateCompanyBriefSections = (data: unknown): CompanyBriefSection[] =>
  validateWithZodSchema(z.array(CompanyBriefSectionSchema), data, 'company brief sections')

export const validateDealBriefSections = (data: unknown): DealBriefSection[] =>
  validateWithZodSchema(z.array(DealBriefSectionSchema), data, 'deal brief sections')

export const validateLpBriefSections = (data: unknown): LpBriefSection[] =>
  validateWithZodSchema(z.array(LpBriefSectionSchema), data, 'LP brief sections')

export const validateProspectBriefSections = (data: unknown): ProspectBriefSection[] =>
  validateWithZodSchema(z.array(ProspectBriefSectionSchema), data, 'prospect brief sections')

export const validateLpFundraisingRows = (data: unknown): LpFundraisingRow[] =>
  validateWithZodSchema(z.array(LpFundraisingRowSchema), data, 'LP fundraising position')

export const validateLpFundraisingHistoryRows = (data: unknown): LpFundraisingHistoryRow[] =>
  validateWithZodSchema(z.array(LpFundraisingHistoryRowSchema), data, 'LP fundraising history')

export const validateInvestmentCriteriaList = (data: unknown): InvestmentCriterion[] =>
  validateWithZodSchema(z.array(InvestmentCriterionSchema), data, 'investment criteria')

export const validateAgentWrittenContentList = (data: unknown): AgentWrittenContent[] =>
  validateWithZodSchema(z.array(AgentWrittenContentSchema), data, 'agent written content list')

export const validateCompanyStatesList = (data: unknown): CompanyState[] =>
  validateWithZodSchema(z.array(CompanyStateSchema), data, 'company states list')

export const validateDealStatesList = (data: unknown): DealState[] =>
  validateWithZodSchema(z.array(DealStateSchema), data, 'deal states list')

export const validateProspectStatesList = (data: unknown): ProspectState[] =>
  validateWithZodSchema(z.array(ProspectStateSchema), data, 'prospect states list')

export const validateLpStatesList = (data: unknown): LpState[] =>
  validateWithZodSchema(z.array(LpStateSchema), data, 'LP states list')

export const validateDocusignEnvelopesList = (data: unknown): DocusignEnvelope[] =>
  validateWithZodSchema(z.array(DocusignEnvelopeSchema), data, 'docusign envelopes list')

export const validateCompanyKpisList = (data: unknown): CompanyKpi[] =>
  validateWithZodSchema(z.array(CompanyKpiSchema), data, 'company kpis list')
